
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import DashboardWrapper from "../../components/DashboardWrapper";
import CrudForm from "../../components/dashboard/form/CrudForm";
import { useProductsCrud } from "../../hooks/useProductsCrud";
import productService from "../../services/productService";
import authService from "../../services/authService";

const VENDOR_ID = "1";

const generateSlug = (text = "") =>
  text.toString().toLowerCase().trim().replace(/\s+/g, "-");

const emptyVariant = () => ({
  variant_size: "",
  // color_name: "",
  unit: "",
  variant_sku: "",
  variant_price: "",
  variant_offer_price: "",
  variant_qty: "",
});

const getResponseArray = (response) => {
  const body = response?.data ?? response;

  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  if (Array.isArray(body?.data?.data)) return body.data.data;
  if (Array.isArray(body?.products?.data)) return body.products.data;

  return [];
};

const isActive = (item) =>
  item?.status === true ||
  item?.status === 1 ||
  item?.status === "1" ||
  item?.status === undefined ||
  item?.status === null;

export default function CreateProduct() {
  const navigate = useNavigate();
  const { createProduct } = useProductsCrud();

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [childCategories, setChildCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  const [sizeOptions, setSizeOptions] = useState([]);
  const [colorOptions, setColorOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);

  const [loading, setLoading] = useState(true);
  const [variants, setVariants] = useState([emptyVariant()]);

  useEffect(() => {
    let mounted = true;

    const loadOptions = async () => {
      try {
        const results = await Promise.allSettled([
          productService.getAllCategories(),
          productService.getAllSubCategories(),
          productService.getAllChildCategories(),
          productService.getAllBrands(),
          productService.getAllProducts(),
          productService.getAllColors(),
          productService.getAllSizes(),
        ]);

        if (!mounted) return;

        const [
          catResult,
          subResult,
          childResult,
          brandResult,
          productResult,
          colorResult,
          sizeResult,
        ] = results;

        const readResult = (result, label) => {
          if (result.status === "fulfilled") {
            return getResponseArray(result.value);
          }

          console.error(`Failed to load ${label}:`, result.reason);
          return [];
        };

        const categoryData = readResult(
          catResult,
          "categories"
        ).filter(isActive);

        let subCategoryData = readResult(
          subResult,
          "subcategories"
        ).filter(isActive);

        let childCategoryData = readResult(
          childResult,
          "child categories"
        ).filter(isActive);

        const brandData = readResult(
          brandResult,
          "brands"
        )
          .filter(isActive)
          .map((brand) => ({
            id: brand.id,
            name: brand.name,
          }));

        subCategoryData = subCategoryData.filter((sub) =>
          categoryData.some(
            (cat) => Number(cat.id) === Number(sub.category_id)
          )
        );

        childCategoryData = childCategoryData.filter((child) =>
          subCategoryData.some(
            (sub) =>
              Number(sub.id) === Number(child.sub_category_id)
          )
        );

        setCategories(categoryData);
        setSubCategories(subCategoryData);
        setChildCategories(childCategoryData);
        setBrands(brandData);

    const productsData = readResult(productResult, "products");

const sizes = new Set();
const units = new Set();

productsData.forEach((product) => {
  const items = Array.isArray(product?.variant_items)
    ? product.variant_items
    : [];

  items.forEach((item) => {
    const size = String(item?.variant_size ?? "").trim();
    const unit = String(item?.unit ?? "").trim();

    if (size) sizes.add(size);
    if (unit) units.add(unit);
  });
});

const allSizes = [...sizes].sort((a, b) =>
  a.localeCompare(b, undefined, { numeric: true })
);

const allUnits = [...units].sort((a, b) => a.localeCompare(b));

setSizeOptions(allSizes);
setUnitOptions(allUnits);

console.log("Variant sizes from API:", allSizes);
console.log("Variant units from API:", allUnits);

console.log("API size options:", allSizes);
console.log("API unit options:", [...units]);
        console.log("Product categories:", categoryData);
        console.log("Product brands:", brandData);
      console.log("Size options:", allSizes);
console.log("Color options:", colorOptions);
console.log("Unit options:", [...units]);
        console.log("API unit options:", [...units]);
      } catch (error) {
        console.error("Failed to load product form options:", error);

        Swal.fire(
          "Error",
          "Failed to load product form options. Please check the API.",
          "error"
        );
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadOptions();

    return () => {
      mounted = false;
    };
  }, []);

  const allowedFields = [
    "name",
    "images",
    "category_id",
    "sub_category_id",
    "child_category_id",
    "brand_id",
    "short_description",
    "long_description",
    "video_link",
    "offer_start_date",
    "offer_end_date",
    "product_type",
    "status",
    "seo_title",
    "seo_description",
    "colors",
  ];

  const initialData = allowedFields.reduce((acc, field) => {
    acc[field] = field === "status" ? 1 : "";
    return acc;
  }, {});

  initialData.is_approved = 0;

  const handleSubmit = useCallback(
    async (formData) => {
      try {
        console.log("Logged in user:", authService.getCurrentUser());

        if (!String(formData.name ?? "").trim()) {
          Swal.fire(
            "Validation",
            "Product name is required.",
            "warning"
          );
          return;
        }

        // Exactly two image files are required.
   
// Allow multiple product images.
const selectedImages = (formData.images || []).filter(
  (image) => image instanceof File
);

if (selectedImages.length === 0) {
  Swal.fire(
    "Validation",
    "Please select at least one product image.",
    "warning"
  );
  return;
}



        // Remove only completely empty rows.
        const validVariants = variants.filter((variant) =>
          Object.values(variant).some(
            (value) => String(value ?? "").trim() !== ""
          )
        );

        if (validVariants.length === 0) {
          Swal.fire(
            "Validation",
            "Please add at least one product variant.",
            "warning"
          );
          return;
        }

        // Validate every variant independently.
        const incompleteVariant = validVariants.some((variant) => {
          const requiredFields = [
            variant.variant_size,
            // variant.color_name,
            variant.unit,
            variant.variant_sku,
            variant.variant_price,
            variant.variant_qty,
          ];

          const price = Number(variant.variant_price);
          const quantity = Number(variant.variant_qty);

          const invalidOfferPrice =
            variant.variant_offer_price !== "" &&
            (
              !Number.isFinite(Number(variant.variant_offer_price)) ||
              Number(variant.variant_offer_price) < 0
            );

          return (
            requiredFields.some(
              (value) => String(value ?? "").trim() === ""
            ) ||
            !Number.isFinite(price) ||
            price < 0 ||
            !Number.isFinite(quantity) ||
            quantity < 0 ||
            invalidOfferPrice
          );
        });

        if (incompleteVariant) {
          Swal.fire(
            "Validation",
            "Fill Size, Color, Unit, SKU, Price and Quantity for every variant. Enter valid non-negative numbers for price and quantity.",
            "warning"
          );
          return;
        }

        // SKUs must be unique within this submission.
        const skus = validVariants.map((variant) =>
          variant.variant_sku.trim().toLowerCase()
        );

        if (new Set(skus).size !== skus.length) {
          Swal.fire(
            "Validation",
            "Duplicate SKU found. Enter a unique SKU for every variant.",
            "warning"
          );
          return;
        }

        const payload = new FormData();

        // Regular product fields
        Object.keys(formData).forEach((key) => {
          const value = formData[key];

          if (key === "images") {
            selectedImages.forEach((image) => {
              payload.append("images", image);
            });
            return;
          }

          if (key === "colors") {
            (Array.isArray(value) ? value : []).forEach((color) => {
              const colorValue =
                typeof color === "string"
                  ? color
                  : color?.name ?? "";

              if (colorValue) {
                payload.append("colors[]", colorValue);
              }
            });
            return;
          }

          if (key === "brand_id") {
            if (
              value !== null &&
              value !== undefined &&
              String(value).trim() !== ""
            ) {
              payload.append("brand_id", String(value));
            }
            return;
          }

          if (value === undefined || value === null) return;

          payload.append(
            key,
            typeof value === "object"
              ? JSON.stringify(value)
              : String(value)
          );
        });

        // Required product-level fields
        payload.set("vendor_id", VENDOR_ID);
        payload.set("slug", generateSlug(formData.name));
        payload.set("is_approved", "0");

        // IMPORTANT:
        // Do not set variant fields before this loop.
        // Do not use payload.set() for individual variants.
        // Every variant is appended using repeated field names.
        validVariants.forEach((variant) => {
          payload.append(
            "variant_size",
            variant.variant_size.trim()
          );

        

          payload.append(
            "unit",
            variant.unit.trim()
          );

          payload.append(
            "variant_sku",
            variant.variant_sku.trim()
          );

          payload.append(
            "variant_price",
            String(Number(variant.variant_price))
          );

        if (
  variant.variant_offer_price !== "" &&
  variant.variant_offer_price !== null &&
  variant.variant_offer_price !== undefined
) {
  payload.append(
    "variant_offer_price",
    String(Number(variant.variant_offer_price))
  );
}

          payload.append(
            "variant_qty",
            String(Number(variant.variant_qty))
          );
        });

        // Debug: verify repeated fields in DevTools console.
        console.log("All variants being submitted:", validVariants);

        for (const [key, value] of payload.entries()) {
          console.log(
            key,
            value instanceof File ? value.name : value
          );
        }

        await createProduct(payload);

        await Swal.fire({
          title: "Success",
          text: "Product created successfully.",
          icon: "success",
          confirmButtonText: "OK",
          confirmButtonColor: "#3b82f6",
        });

        navigate("/admin/all-products");
      } catch (error) {
        console.error(
          "Create product failed:",
          error.response?.data || error
        );

        const detail = error.response?.data?.detail;

        const errorMessage =
          typeof detail === "string"
            ? detail
            : Array.isArray(detail)
              ? detail
                  .map((item) => item.msg || JSON.stringify(item))
                  .join(", ")
              : error.response?.data?.message ||
                error.message ||
                "Failed to create product";

        Swal.fire("Error", errorMessage, "error");
      }
    },
    [createProduct, navigate, variants]
  );

  return (
    <DashboardWrapper>
      <div className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <button
            type="button"
            className="btn btn-secondary d-flex align-items-center justify-content-between"
            onClick={() => navigate(-1)}
          >
            <span>Back</span>
          </button>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <h5 className="card-header">Create Product</h5>

              <div className="card-body demo-vertical-spacing demo-only-element">
                {!loading ? (
                  <CrudForm
                    initialData={initialData}
                    onSubmit={handleSubmit}
                    allowedFields={allowedFields}
                    categories={categories}
                    subCategories={subCategories}
                    childCategories={childCategories}
                    brands={brands}
                    sizeOptions={sizeOptions}
                    colorOptions={colorOptions}
                    unitOptions={unitOptions}
                    variants={variants}
                     showVariants={true}
                    onVariantsChange={setVariants}
                    fileUploadStyle="product"
                    fileFields={[
                      { name: "images", multiple: true },
                    ]}
                  />
                ) : (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: "200px" }}
                  >
                    <div
                      className="spinner-border text-primary"
                      role="status"
                    >
                      <span className="visually-hidden">
                        Loading...
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardWrapper>
  );
}
